import { clamp_risk_score, normalize_text } from './view_case_details_helpers';

const RISK_ACTIONS = {
  high: {
    key: 'decline',
    title: 'Decline the claim or escalate it for senior review.',
    body: 'The assessed risk is high. Do not approve payment without resolving the risk drivers or recording a senior override.',
  },
  medium: {
    key: 'dispatch',
    title: 'Dispatch an inspector before deciding.',
    body: 'The assessed risk is material. Validate the key risk drivers before approving or declining the claim.',
  },
  low: {
    key: 'payout',
    title: 'Authorise payout once the case facts are complete.',
    body: 'The assessed risk is low enough to proceed, provided the required case facts and documents are complete.',
  },
};

function primary_runtime(component) {
  return Array.isArray(component.display_sla_runtimes)
    ? component.display_sla_runtimes[0]
    : null;
}

function risk_level(panel = {}) {
  const band = normalize_text(panel?.band).toLowerCase();
  if (['critical', 'severe', 'high'].some((label) => band.includes(label))) return 'high';
  if (['medium', 'moderate'].some((label) => band.includes(label))) return 'medium';
  if (['low', 'minimal'].some((label) => band.includes(label))) return 'low';

  const score = clamp_risk_score(panel?.score);
  if (score >= 67) return 'high';
  if (score >= 34) return 'medium';
  return 'low';
}

function action_buttons(recommended_key) {
  const payout_is_override = recommended_key !== 'payout';
  const actions = [
    {
      key: 'dispatch',
      label: 'Dispatch inspector',
      modal_title: 'Dispatch inspector',
      confirm_label: 'Confirm dispatch',
      modal_body: 'Confirm dispatch of an on-site inspector. Estimated arrival is within 24 hours.',
    },
    {
      key: 'payout',
      label: payout_is_override ? 'Pay anyway (override)' : 'Authorise payout',
      modal_title: payout_is_override ? 'Authorise payout (override)' : 'Authorise payout',
      confirm_label: 'Authorise payout',
      modal_body: payout_is_override
        ? 'You are overriding the risk-based recommendation. This is logged under your name in the audit trail.'
        : 'Confirm the payout recommended by the current low-risk assessment.',
      is_override: payout_is_override,
    },
    {
      key: 'decline',
      label: 'Decline claim',
      modal_title: 'Decline claim',
      confirm_label: 'Decline claim',
      modal_body: 'Confirm that this claim should be declined. This decision will be logged in the audit trail.',
    },
  ].map((action) => ({ ...action, recommended: action.key === recommended_key }));

  return actions.sort((left, right) => Number(right.recommended) - Number(left.recommended));
}

function workflow_context(component) {
  const current = normalize_text(component.current_workflow_status?.name);
  const next = normalize_text(component.primary_next_workflow_status?.name);
  if (!next) return '';
  return ` The next available workflow transition is from ${current || 'the current status'} to ${next}.`;
}

function risk_action_card(component, runtime) {
  const score = clamp_risk_score(component.risk_panel?.score);
  const level = risk_level(component.risk_panel);
  const recommendation = RISK_ACTIONS[level];
  const backend_band = normalize_text(component.risk_panel?.band);
  const band = backend_band || `${level[0].toUpperCase()}${level.slice(1)}`;

  return {
    title: recommendation.title,
    by: runtime?.due_label ? `By ${runtime.due_label}` : 'No due date',
    confidence: `${band} risk · ${score}/100`,
    exposure: normalize_text(runtime?.status_label) || 'No active SLA',
    body: `${recommendation.body}${workflow_context(component)}`,
    recommended_action: recommendation.key,
    actions: action_buttons(recommendation.key),
    has_action: true,
  };
}

function unavailable_card(title, body, runtime = null) {
  return {
    title,
    by: 'Pending',
    confidence: 'Risk assessment pending',
    exposure: normalize_text(runtime?.status_label) || 'Not available',
    body,
    recommended_action: '',
    actions: [],
    has_action: false,
  };
}

export function sensible_action_card(component) {
  const runtime = primary_runtime(component);

  if (component.loading_risk_panel) {
    return unavailable_card(
      'Calculating the next sensible action...',
      'The recommendation will be available when the backend risk assessment has loaded.',
      runtime
    );
  }

  if (component.has_risk_panel) {
    return risk_action_card(component, runtime);
  }

  if (component.selected_application_id) {
    return unavailable_card(
      'Complete the risk assessment to get a recommendation.',
      component.risk_panel_message || 'No backend risk result is available for this case.',
      runtime
    );
  }

  return unavailable_card(
    'Open a case to see the next sensible action.',
    'The recommendation will be based on the case risk result.'
  );
}
